import React, { useMemo, useCallback, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, CircularProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../utils/config';

interface Property {
  propertyId: string;
  propertyName: string;
  hotelClass: string;
  propertyAddress: string;
  brand: string;
  rooms: number;
  latitude?: number | null;
  longitude?: number | null;
}

interface PropertyMapModalProps {
  open: boolean;
  onClose: () => void;
  properties: Property[];
  marketName: string;
}

export const PropertyMapModal: React.FC<PropertyMapModalProps> = ({ open, onClose, properties, marketName }) => {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  // Filter properties that have valid coordinates
  const mappableProperties = useMemo(() => {
    return properties.filter(
      (p) => p.latitude != null && p.longitude != null && p.latitude !== 0 && p.longitude !== 0
    );
  }, [properties]);

  // Calculate map bounds to fit all markers
  const { center, zoom } = useMemo(() => {
    if (mappableProperties.length === 0) {
      return { center: { lat: 0, lng: 0 }, zoom: 2 };
    }

    if (mappableProperties.length === 1) {
      return {
        center: { lat: mappableProperties[0].latitude!, lng: mappableProperties[0].longitude! },
        zoom: 14,
      };
    }

    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const p of mappableProperties) {
      if (p.latitude! < minLat) minLat = p.latitude!;
      if (p.latitude! > maxLat) maxLat = p.latitude!;
      if (p.longitude! < minLng) minLng = p.longitude!;
      if (p.longitude! > maxLng) maxLng = p.longitude!;
    }

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    // Estimate zoom level from bounds
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);
    let estimatedZoom = 12;
    if (maxDiff > 100) estimatedZoom = 2;
    else if (maxDiff > 50) estimatedZoom = 3;
    else if (maxDiff > 20) estimatedZoom = 4;
    else if (maxDiff > 10) estimatedZoom = 5;
    else if (maxDiff > 5) estimatedZoom = 6;
    else if (maxDiff > 2) estimatedZoom = 7;
    else if (maxDiff > 1) estimatedZoom = 8;
    else if (maxDiff > 0.5) estimatedZoom = 9;
    else if (maxDiff > 0.2) estimatedZoom = 10;
    else if (maxDiff > 0.1) estimatedZoom = 11;

    return { center: { lat: centerLat, lng: centerLng }, zoom: estimatedZoom };
  }, [mappableProperties]);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      if (mappableProperties.length > 1) {
        const bounds = new google.maps.LatLngBounds();
        mappableProperties.forEach((p) => {
          bounds.extend({ lat: p.latitude!, lng: p.longitude! });
        });
        map.fitBounds(bounds, 50);
      }
    },
    [mappableProperties]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          m: '50px',
          width: 'calc(100% - 100px)',
          height: 'calc(100% - 100px)',
          maxWidth: 'none',
          maxHeight: 'none',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
        <Box>
          <Typography variant="h6" component="span">
            Properties Map - {marketName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }} component="span">
            {mappableProperties.length} of {properties.length} properties with coordinates
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedProperty && (
            <Typography variant="body1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {selectedProperty.propertyName}
            </Typography>
          )}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error">Failed to load Google Maps. Please try again later.</Typography>
          </Box>
        )}
        {!isLoaded && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {isLoaded && !loadError && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={zoom}
            onLoad={onMapLoad}
            options={{
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: false,
            }}
          >
            {mappableProperties.map((property) => (
              <MarkerF
                key={property.propertyId}
                position={{ lat: property.latitude!, lng: property.longitude! }}
                title={property.propertyName}
                onClick={() => setSelectedProperty(property)}
              />
            ))}
            {selectedProperty && selectedProperty.latitude != null && selectedProperty.longitude != null && (
              <InfoWindowF
                position={{ lat: selectedProperty.latitude, lng: selectedProperty.longitude }}
                onCloseClick={() => setSelectedProperty(null)}
              >
                <Box sx={{ maxWidth: 280, mt: -1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5, pr: 2 }}>
                    {selectedProperty.propertyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {selectedProperty.propertyAddress}
                  </Typography>
                  <Typography variant="body2">
                    {selectedProperty.rooms} rooms | {selectedProperty.hotelClass}
                  </Typography>
                  {selectedProperty.brand && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedProperty.brand}
                    </Typography>
                  )}
                </Box>
              </InfoWindowF>
            )}
          </GoogleMap>
        )}
      </DialogContent>
    </Dialog>
  );
};
